import { HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdminApiError } from './admin-api';
import { AdminHttpClient } from './admin-http-client.service';
import { AdminSession } from './models';

const SESSION_KEY = 'crewjam_admin_session';
const TOKEN_KEY = 'crewjam_admin_token';
const LOGGED_OUT_KEY = 'crewjam_admin_logged_out';

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private readonly api = inject(AdminHttpClient);
  private readonly session = signal<AdminSession | null>(this.restoreSession());
  readonly loggedOut = signal(sessionStorage.getItem(LOGGED_OUT_KEY) === 'true');
  readonly initializationError = signal('');
  readonly initializing = signal(false);
  readonly localTokenLogin = environment.localTokenLogin ?? false;

  readonly currentAdmin = this.session.asReadonly();
  readonly currentRole = computed(() => this.session()?.permissions ?? null);
  readonly authenticated = computed(() => !!this.session()?.token);
  readonly canManageAdmins = computed(() => this.currentRole() === 'ROOT');
  readonly canDeleteGames = computed(() => {
    const role = this.currentRole();
    return role === 'ROOT' || role === 'EDITOR';
  });

  async initialize(cloudflareToken = '', force = false): Promise<void> {
    if (this.loggedOut() && !force) return;
    if (this.initializing()) return;
    this.initializing.set(true);
    this.initializationError.set('');
    try {
      const session = await firstValueFrom(this.api.post<AdminSession>(
        '/admin-api/auth/sessions',
        null,
        true,
        cloudflareToken ? new HttpHeaders({ 'Cf-Access-Jwt-Assertion': cloudflareToken }) : undefined,
      ));
      this.setSession(session);
    } catch (error) {
      const apiError = error instanceof AdminApiError ? error : null;
      if (apiError && ['EX4010', 'EX4013', 'EX4014', 'EX4030'].includes(apiError.code)) this.clearSession();
      this.initializationError.set(apiError?.message || '無法建立管理者登入狀態，請重新通過 Cloudflare Access 後再試。');
    } finally {
      this.initializing.set(false);
    }
  }

  submitLocalToken(token: string): void {
    const value = token.trim();
    if (!value) {
      this.initializationError.set('請貼上 Cloudflare Access token。');
      return;
    }
    void this.initialize(value, true);
  }

  login(): void { void this.initialize('', true); }

  logout(): void {
    this.clearSession();
    this.initializationError.set('');
    this.loggedOut.set(true);
    sessionStorage.setItem(LOGGED_OUT_KEY, 'true');
  }

  clearSession(): void {
    this.session.set(null);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  }

  private setSession(session: AdminSession): void {
    this.session.set(session);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    sessionStorage.setItem(TOKEN_KEY, session.token);
    sessionStorage.removeItem(LOGGED_OUT_KEY);
    this.loggedOut.set(false);
    this.initializationError.set('');
  }

  private restoreSession(): AdminSession | null {
    try {
      const value = sessionStorage.getItem(SESSION_KEY);
      return value ? JSON.parse(value) as AdminSession : null;
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      return null;
    }
  }
}
